import { useState } from 'react';
import { fmtMoney, useT } from '../lib/i18n';
import { haptic } from '../lib/telegram';

/**
 * «Как заплатили»: Наличные / Revolut (cards too) / Банк — wherever money
 * comes in (server: booking/payments.ts, PAY_METHODS; sql/040). One is
 * always picked: the one this device used last, or «Наличные». No money
 * changing hands («Списать с баланса», «Использовать кредит») — no picker.
 * Mockup: docs/mockups/payment-methods.html.
 */
export type PayMethod = 'cash' | 'revolut' | 'bank';
export const PAY_METHODS: PayMethod[] = ['cash', 'revolut', 'bank'];

/** Per device, for convenience only: a cleared or blocked storage just means «Наличные». */
const KEY = 'coachbot.payMethod';
export function lastMethod(): PayMethod {
  try {
    const v = localStorage.getItem(KEY);
    if (v && (PAY_METHODS as string[]).includes(v)) return v as PayMethod;
  } catch { /* private mode, blocked storage */ }
  return 'cash';
}
/** Called once a payment with this method has been saved. */
export function rememberMethod(m: PayMethod) {
  try { localStorage.setItem(KEY, m); } catch { /* not remembered, that is all */ }
}
export const useMethod = () => useState<PayMethod>(lastMethod);

type T = ReturnType<typeof useT>;
export const methodLabel = (t: T, m: PayMethod | 'none') => t(`pm.${m}`);

export function MethodPicker({ value, onChange }: { value: PayMethod; onChange: (m: PayMethod) => void }) {
  const t = useT();
  return (
    <div className="field">
      <span className="field__label">{t('pm.label')}</span>
      <div className="pmethod" role="radiogroup" aria-label={t('pm.label')}>
        {PAY_METHODS.map((m) => (
          <button key={m} type="button" role="radio" aria-checked={value === m} aria-pressed={value === m}
            onClick={() => { haptic.tap(); onChange(m); }}>
            {methodLabel(t, m)}
            {m !== 'cash' && <small>{t(`pm.${m}Hint`)}</small>}
          </button>
        ))}
      </div>
    </div>
  );
}

/** One period's «Как получено», from the same buckets as «Получено» (components/MoneyChart.tsx). */
export type MethodTotals = Record<string, { amount: number; n: number }>;
const ORDER: (PayMethod | 'none')[] = ['revolut', 'cash', 'bank', 'none'];

export function MethodBreakdown({ totals }: { totals: MethodTotals }) {
  const t = useT();
  const rows = Object.entries(totals).filter(([, v]) => v.amount > 0)
    // Biggest first; «Не указано» always last.
    .sort(([a, x], [b, y]) => (a === 'none' ? 1 : 0) - (b === 'none' ? 1 : 0) || y.amount - x.amount
      || ORDER.indexOf(a as PayMethod) - ORDER.indexOf(b as PayMethod));
  const total = rows.reduce((n, [, v]) => n + v.amount, 0);
  if (total <= 0) return null;
  const count = rows.reduce((n, [, v]) => n + v.n, 0);
  const pct = (v: number) => Math.round((v / total) * 100);
  return (
    <div className="pmbreak">
      <div className="pmbreak__head">
        <b>{t('pm.breakdown')}</b>
        <span>{t('pm.payments', { n: count, payments: t.word('pm.paymentsW', count) })}</span>
      </div>
      <div className="pmbreak__bar" aria-hidden="true">
        {rows.map(([k, v]) => <i key={k} className={`pm-${k}`} style={{ width: `${(v.amount / total) * 100}%` }} />)}
      </div>
      {rows.map(([k, v]) => (
        <div key={k} className={`pmbreak__row${k === 'none' ? ' pmbreak__row--none' : ''}`}>
          <span className={`pmbreak__dot pm-${k}`} />
          <span>{methodLabel(t, k as PayMethod | 'none')}</span>
          <b>{fmtMoney(Math.round(v.amount * 100) / 100)}</b>
          <small>
            {k === 'none'
              ? t('pm.noneHint')
              : `${t('pm.payments', { n: v.n, payments: t.word('pm.paymentsW', v.n) })} · ${pct(v.amount)}%`}
          </small>
        </div>
      ))}
    </div>
  );
}
